import ActivityReportTextDialog from "@/ui/pages/activity_report/activityReportTextDialog";
import Logo from "@/ui/components/Logo";
import ContinueInAppInner from "@/ui/components/ContinueInAppInner";
// import { X } from "lucide-preact";
import { useState, useEffect } from "preact/hooks";
import {
  // getAssessmentPdfUrl,
  getLocalizationPack,
  postEvent,
  getInstallQrCode,
} from "@/lib/api_controller";
import { toTitleCase } from "@/lib/strings";
import { localStorageGet, localStorageSet, STORAGE_KEYS } from "@/lib/storage";
import { uiLangIsRtl } from "@/l11n/l11n";
import { TaaliaEvent } from "@/lib/event_types";
import { doLog } from "@/lib/globals";
import AppDownloadModal from "@/ui/components/AppDownloadModal";
import { isDesktop } from "@/lib/web";

const ActivityReportView = ({ assessmentParam, closeModal }) => {
  const [l11n, setL11n] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [rtlState, setRtlState] = useState(() => ({
    targetLangIsRtl: localStorageGet(STORAGE_KEYS.targetLangIsRtl),
    uiIsRtl: uiLangIsRtl(),
  }));

  useEffect(() => {
    postEvent(TaaliaEvent.activityReportOpened);
    localStorageSet(STORAGE_KEYS.activityReportViewedKey, true);
  }, []);

  // Use the assessmentParam if it is provided, otherwise use the assessment from session storage
  const [assessment] = useState(
    assessmentParam ?? localStorageGet(STORAGE_KEYS.completedAssessmentKey)
  );

  useEffect(() => {
    const targetLangIsRtl = localStorageGet(STORAGE_KEYS.targetLangIsRtl);
    const uiIsRtl = uiLangIsRtl();
    setRtlState({
      targetLangIsRtl,
      uiIsRtl,
    });

    async function fetchData() {
      // Get localization
      const localization = await getLocalizationPack("assessment_report");
      setL11n(localization);

      // Only show QR code on desktop
      if (isDesktop) {
        const qrCodeData = await getInstallQrCode();
        setQrCode(qrCodeData);
      }

      // Set loading to false
      setIsLoading(false);
    }
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        ...
      </div>
    );
  }

  if (!assessment) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        ERROR: No assessment available.
      </div>
    );
  }

  // MARK: Main Render
  return (
    <div
      style={{
        position: "relative",
        backgroundColor: "white",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "0px",
        width: "100%",
        maxWidth: "500px",
        margin: "2.0rem auto 2.0rem auto", // top, left, bottom, right
        boxSizing: "border-box",
      }}
    >
      <Header l11n={l11n} assessment={assessment} closeModal={closeModal} />
      <OverallScore l11n={l11n} score={assessment.score} uiIsRtl={rtlState.uiIsRtl} />
      <Summary l11n={l11n} assessment={assessment} uiIsRtl={rtlState.uiIsRtl} />
      <ModuleSection
        l11n={l11n}
        assessment={assessment}
        uiIsRtl={rtlState.uiIsRtl}
        targLangIsRtl={rtlState.targetLangIsRtl}
        qrCode={qrCode}
      />
      <Footer l11n={l11n} assessment={assessment} uiIsRtl={rtlState.uiIsRtl} />
      <AppDownloadModal />
    </div>
  );
};

// MARK: Header
const Header = ({ l11n, assessment }) => (
  <div
    style={{
      position: "relative",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      backgroundColor: "white",
    }}
    className="mb-6"
  >
    <div style={{ display: "flex", alignItems: "center", backgroundColor: "white" }}>
      {/* <span
        onClick={closeModal}
        style={{ width: "100px", fontSize: "24px", cursor: "pointer", textAlign: "left" }}
      >
        <X size={26} />
      </span> */}
      <div
        style={{
          flex: 1,
          fontSize: "20px",
          fontWeight: "bold",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="mb-3">
          <Logo />
        </div>
        {l11n.assessment_report_title}
        <div className="text-sm">{new Date(assessment.createdAt).toLocaleString()}</div>
        {/* <DownloadLink l11n={l11n} assessmentId={assessment.id} /> */}
      </div>
    </div>
  </div>
);

// // MARK: DownloadLink
// const DownloadLink = ({ l11n, assessmentId }) => {
//   const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
//   return (
//     <a
//       href="#"
//       onClick={(e) => {
//         e.preventDefault(); // Prevents the page refresh
//         onPdfUrlClick(lcid, assessmentId);
//       }}
//       style={{
//         fontSize: "14px",
//         textDecoration: "none",
//         color: "blue",
//         wordWrap: "break-word",
//         overflowWrap: "break-word",
//       }}
//     >
//       {l11n.download_pdf}
//     </a>
//   );
// };

// // MARK: onPdfUrlClick
// async function onPdfUrlClick(lcid, assessmentId) {
//   const url = await getAssessmentPdfUrl(lcid, assessmentId);
//   window.open(url, "_blank");
// }

// MARK: OverallScore
const OverallScore = ({ l11n, score, uiIsRtl }) => {
  if (!score) return <div>not_found</div>;
  const overallCefrScore = score.overall_cefr || "Unknown";

  return (
    <div
      style={{
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        textAlign: uiIsRtl ? "right" : "left",
        direction: uiIsRtl ? "rtl" : "ltr",
        backgroundColor: "white",
      }}
    >
      <div
        style={{
          fontSize: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0px 6px",
        }}
      >
        <div style={{ fontWeight: "bold" }}>{l11n.cefr_level}:</div>
        <div style={{ fontWeight: "bold", textTransform: "uppercase" }}>
          {overallCefrScore}
        </div>
      </div>
      <hr
        style={{
          width: "100%",
          border: "0.5px solid rgb(107, 107, 107)",
        }}
        className="mb-2"
      />
      {score.skill_scores && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            margin: "0px 8px",
          }}
        >
          {Object.entries(score.skill_scores).map(([key, value]) => {
            let displayKey = key;
            if (key.toLowerCase() == "vocabulary_score") {
              displayKey = "vocab";
            } else if (key.toLowerCase() == "combined_score") {
              // Skip combined score
              return null;
            } else if (key.toLowerCase() == "fluency_score") {
              displayKey = "fluency";
            } else if (key.toLowerCase() == "relevance_score") {
              displayKey = "relevance";
            } else if (key.toLowerCase() == "speed_score") {
              displayKey = "speed";
            }
            let displayValue = l11n[displayKey];
            if (!displayValue) {
              console.warn(`No display value for key: ${key}`);
              displayValue = key;
            }
            displayValue = toTitleCase(displayValue);
            return (
              <div key={key} style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{displayValue}</span>
                <span>{value} / 6.0</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// MARK: Summary
const Summary = ({ l11n, assessment, uiIsRtl }) => {
  const comments = assessment.score?.comments?.[assessment.scriptLang];
  if (!comments) return null;
  return (
    <div
      style={{
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        textAlign: uiIsRtl ? "right" : "left",
        direction: uiIsRtl ? "rtl" : "ltr",
        backgroundColor: "white",
      }}
    >
      <div
        style={{
          padding: "0px 6px",
          fontSize: "24px",
          fontWeight: "bold",
          textTransform: "uppercase",
        }}
      >
        {l11n.summary}
      </div>
      <hr
        style={{
          width: "100%",
          border: "0.5px solid rgb(107, 107, 107)",
        }}
        className="mb-2"
      />
      <div style={{ margin: "0px 8px", color: "rgb(187, 99, 4)" }}>{comments}</div>
    </div>
  );
};

// MARK: ModuleSection
const ModuleSection = ({ l11n, assessment, uiIsRtl, targLangIsRtl, qrCode }) => {
  const content = (() => {
    switch (assessment.module.type) {
      case "dialog_text":
        return (
          <ActivityReportTextDialog
            l11n={l11n}
            assessment={assessment}
            uiIsRtl={uiIsRtl}
            targLangIsRtl={targLangIsRtl}
          />
        );
      default:
        return <div>Unknown module type</div>;
    }
  })();

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          filter: "blur(8px)",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        {content}
      </div>
      <div
        style={{
          position: "absolute",
          top: "72px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          zIndex: 1,
        }}
      >
        <GetAppRow uiIsRtl={uiIsRtl} qrCode={qrCode} />
      </div>
    </div>
  );
};

// MARK: Footer
const Footer = ({ l11n, assessment, uiIsRtl }) => (
  <div
    style={{
      marginTop: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      color: "rgb(107, 107, 107)",
      fontSize: "14px",
      // textAlign: uiIsRtl ? "right" : "left",
      direction: uiIsRtl ? "rtl" : "ltr",
    }}
  >
    <hr style={{ width: "100%", border: "0.5px solid rgb(107, 107, 107)" }} />
    <div style={{ fontWeight: "bold", fontSize: "16px" }}>
      {l11n.thanks_for_assessment}
    </div>
    <div>
      {l11n.have_a_question}{" "}
      <a href="mailto:team@taalia.com">
        <span style={{ fontWeight: "bold" }}>team@taalia.com</span>
      </a>
    </div>
    <hr style={{ width: "100%", border: "0.5px solid rgb(107, 107, 107)" }} />
    <div>
      <span style={{ fontWeight: "bold" }}>{l11n.generated_at}:</span>{" "}
      {new Date().toLocaleString()} <br />
      <span style={{ fontWeight: "bold" }}>{l11n.for_assessment}:</span> {assessment.id}
    </div>
    <div style={{ fontSize: "12px" }}>&copy; 2025 Vondelbaan, B.V.</div>
  </div>
);

// MARK: GetAppRow
const GetAppRow = ({ uiIsRtl, qrCode }) => {
  if (doLog()) console.log("qrCode", qrCode);
  return (
    <div
      style={{
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        textAlign: uiIsRtl ? "right" : "left",
        direction: uiIsRtl ? "rtl" : "ltr",
        backgroundColor: "rgba(255, 255, 255, 0.98)",
        borderRadius: "16px",
        boxShadow: "0 8px 32px rgba(255, 168, 75, 0.3), 0 4px 24px rgba(0, 0, 0, 0.15)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255, 168, 75, 0.3)",
        transform: "translateY(-2px)",
        transition: "transform 0.2s ease-out, box-shadow 0.2s ease-out",
      }}
    >
      <ContinueInAppInner headlineKey="view_full_report_in_app" />
      <div className="flex justify-center items-center w-full">
        {qrCode && (
          <div
            className="w-[144px] h-[144px] overflow-hidden"
            dangerouslySetInnerHTML={{ __html: qrCode }}
          />
        )}
      </div>
    </div>
  );
};

export default ActivityReportView;
